import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { notify, toastIds } from '../utils/notify.jsx';
import { BoltIcon } from '@heroicons/react/24/outline';
import DirectNotification from '../components/DirectNotification';
import { useStatusTracking } from '../contexts/StatusTrackingContext';
import { doc, updateDoc, collection, query, where, orderBy, limit, getDoc, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import BookSpread from '../components/BookDesign/BookSpread';
import BookDesignTopNavLayout from '../layouts/BookDesignTopNavLayout';
import LoadingOverlay from '../components/LoadingOverlay/LoadingOverlay';
import { ensureDesignPrompts, ensureDesignPromptsCheck } from '../services/bookService/operations/ensureDesignPrompts';
import ImageLoadingPlaceholder from '../components/BookDesign/elements/ImageLoadingPlaceholder';
import { PRINT_LOADING_STATES } from '../components/LoadingOverlay/LoadingStates';
import { useBook } from '../contexts/BookContext';
import { useDimensions } from '../contexts/DimensionContext';
import PageSizeSelector from '../components/PageSizeSelector/PageSizeSelector';
import GenerateAllImagesModal from '../components/BookDesign/modals/GenerateAllImagesModal';
import AudioPlaylistPlayer from '../components/BookDesign/components/AudioPlaylistPlayer';
import GenerateAllAudioModal from '../components/BookDesign/modals/GenerateAllAudioModal';
import NarrationOrderModal from '../components/BookDesign/modals/NarrationOrderModal';
import WorldObjectsConsistencyModal from '../components/BookDesign/components/WorldObjectsConsistencyModal';
import { getImportantObjects } from '../utils/getImportantObjects';


export default function DesignPage() {
  // TEST TOAST BUTTON function
  const testToast = useCallback(() => {
    console.log('TEST TOAST BUTTON CLICKED');
    notify.info('This is a test toast to verify toast functionality', { toastId: 'design-test-toast' });
  }, []);
  
  // State for monitoring preview processing
  const [previewProcessingId, setPreviewProcessingId] = useState(null);
  const [previewProcessingStatus, setPreviewProcessingStatus] = useState(null);
  const [previewProcessingListener, setPreviewProcessingListener] = useState(null);
  
  // Create a ref to track which book IDs have completed processing
  // We'll use this to stop monitoring once we know processing is complete
  const completedBookIds = React.useRef({});
  
  // Modified approach: Only clear certain toasts on first mount, not on every navigation
  React.useEffect(() => {
    console.log('🔔 DesignPage mounted - initializing toast handling');
    
    // Clear only stale/outdated toast flags
    const now = Date.now();
    const oneHourAgo = now - (60 * 60 * 1000); // 1 hour in milliseconds
    
    // Only remove old toast entries (>1 hour)
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes('DESIGN_READY_TIMESTAMP')) {
        try {
          const timestamp = parseInt(localStorage.getItem(key), 10);
          if (isNaN(timestamp) || timestamp < oneHourAgo) {
            console.log('Removing outdated toast flag:', key);
            localStorage.removeItem(key);
            // Also remove the corresponding non-timestamp keys
            const baseKey = key.replace('_TIMESTAMP', '');
            localStorage.removeItem(baseKey);
          }
        } catch (e) {}
      }
    }
    
    // Get the book ID and check status
    const urlParts = window.location.pathname.split('/');
    const bookId = urlParts.length > 2 ? urlParts[2] : null;
    
    // Check if we need to override window.showDesignReadyToast
    if (bookId) {
      console.log(`Checking status for book ${bookId}`);
      
      // Override the global toast function to do nothing if status is completed
      const originalShowToast = window.showDesignReadyToast;
      window.showDesignReadyToast = (id) => {
        // Get current status directly from Firebase
        const status = window.getCurrentProcessingStatus?.(id || bookId);
        console.log(`🔔 Design status check: ${status}`);
        
        if (status === 'completed') {
          console.log('🔔 Completed status - not showing design toast');
          // Don't dismiss all toasts, just don't show a new one
          return;
        }
        
        // Show toast for any of the image generation or updating statuses
        if (status === 'generatingCharacter' || status === 'generatingCoverImage' || status === 'updatingImageElements') {
          console.log(`✅ ${status} status - showing toast`);
          notify.info('Design is ready! You can continue while images are being generated.', {
            toastId: 'design-ready-toast',
            position: 'bottom-right',
            autoClose: 8000,
            closeOnClick: true,
            draggable: true
          });
        } else {
          console.log(`Status is ${status} - not showing toast`);
        }
      };
    }
  }, [])
  
  const navigate = useNavigate();
  const location = useLocation();
  const { bookId, spreadId } = useParams();
  const { currentBook, loading, error, loadBook } = useBook();
  const initialSpreadId = spreadId ? parseInt(spreadId, 10) : 0;
  
  // Clear preview processing state on mount to prevent stale loading UI from /create
  useEffect(() => {
    // Clear the preview processing listener if it exists
    if (previewProcessingListener) {
      previewProcessingListener();
      setPreviewProcessingListener(null);
    }
    setPreviewProcessingId(null);
    setPreviewProcessingStatus(null);
  }, []);

  // Don't redirect based on characterData - it's deleted during generation and will be repopulated
  // The loading UI on WorldPage will handle showing when generation is in progress
  const { updatePageSize, setCurrentBookPageSize } = useDimensions();
  const { bookProcessingStatuses, updateBookProcessingStatus } = useStatusTracking();
  
  const [isLoadingPrint, setIsLoadingPrint] = useState(false);
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [isEnsuringPrompts, setIsEnsuringPrompts] = useState(false);
  const [isGenerateAllModalOpen, setIsGenerateAllModalOpen] = useState(false);
  const [showConsistencyModal, setShowConsistencyModal] = useState(false);
  const [isGenerateAllAudioOpen, setIsGenerateAllAudioOpen] = useState(false);
  const [isNarrationOrderOpen, setIsNarrationOrderOpen] = useState(false);
  const [narrationOrderSpreadId, setNarrationOrderSpreadId] = useState(null);
  const [toastShown, setToastShown] = useState(false);
  const [designRefreshNonce, setDesignRefreshNonce] = useState(0);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const initialCheckRunRef = React.useRef(false);
  // Track concurrent ensureDesignPrompts calls so the overlay stays until all finish
  const ensureInFlightCountRef = React.useRef(0);
  const ensureOverlayStartMsRef = React.useRef(null);
  const ensureOverlayHideTimeoutRef = React.useRef(null);
  const ensureOverlayShowTimeoutRef = React.useRef(null);
  const MIN_OVERLAY_MS = 900; // prevent flicker if a second fast call returns job_running
  const SHOW_OVERLAY_DELAY_MS = 700; // only show UI if call lasts beyond this
  const startEnsure = React.useCallback(() => {
    // If this is the first in-flight, schedule a delayed show of the overlay
    if ((ensureInFlightCountRef.current || 0) === 0) {
      if (ensureOverlayHideTimeoutRef.current) {
        clearTimeout(ensureOverlayHideTimeoutRef.current);
        ensureOverlayHideTimeoutRef.current = null;
      }
      // Clear any pending show and schedule delayed display
      if (ensureOverlayShowTimeoutRef.current) {
        clearTimeout(ensureOverlayShowTimeoutRef.current);
        ensureOverlayShowTimeoutRef.current = null;
      }
      ensureOverlayStartMsRef.current = null;
      ensureOverlayShowTimeoutRef.current = setTimeout(() => {
        ensureOverlayStartMsRef.current = Date.now();
        setIsEnsuringPrompts(true);
      }, SHOW_OVERLAY_DELAY_MS);
    }
    ensureInFlightCountRef.current = (ensureInFlightCountRef.current || 0) + 1;
  }, []);
  const finishEnsure = React.useCallback(() => {
    ensureInFlightCountRef.current = Math.max(0, (ensureInFlightCountRef.current || 0) - 1);
    if (ensureInFlightCountRef.current === 0) {
      // If overlay hasn't shown yet, cancel the pending show and exit
      if (ensureOverlayShowTimeoutRef.current) {
        clearTimeout(ensureOverlayShowTimeoutRef.current);
        ensureOverlayShowTimeoutRef.current = null;
      }
      if (!ensureOverlayStartMsRef.current) {
        setIsEnsuringPrompts(false);
        return;
      }
      const started = ensureOverlayStartMsRef.current || 0;
      const elapsed = Date.now() - started;
      const wait = Math.max(0, MIN_OVERLAY_MS - elapsed);
      if (ensureOverlayHideTimeoutRef.current) {
        clearTimeout(ensureOverlayHideTimeoutRef.current);
      }
      ensureOverlayHideTimeoutRef.current = setTimeout(() => {
        setIsEnsuringPrompts(false);
        ensureOverlayStartMsRef.current = null;
        ensureOverlayHideTimeoutRef.current = null;
      }, wait);
    }
  }, []);
  // Track whether we've already performed the initial delayed load
  const initialLoadDoneRef = React.useRef(false);
  // Smooth, local progress for the ensureDesignPrompts overlay (UI-only)
  const [ensurePromptsProgress, setEnsurePromptsProgress] = useState(0);
  useEffect(() => {
    let timer = null;
    if (isEnsuringPrompts) {
      setEnsurePromptsProgress(35);
      timer = setInterval(() => {
        setEnsurePromptsProgress((p) => (p < 85 ? p + 3 : 85));
      }, 500);
    } else {
      if (timer) clearInterval(timer);
      setEnsurePromptsProgress(0);
    }
    return () => { if (timer) clearInterval(timer); };
  }, [isEnsuringPrompts]);

  // Label which data source drives prompt generation (purely UI)
  const promptSourceLabel = React.useMemo(() => {
    try {
      const w = currentBook?.world;
      const hasWorld = !!w && typeof w === 'object' && Object.keys(w).length > 0;
      if (hasWorld) return 'World Details';
      return 'Story Details';
    } catch (_) {
      return 'Story Details';
    }
  }, [currentBook?.world]);

  // Build playlist of all text elements with audio
  const narrationItems = React.useMemo(() => {
    try {
      const spreads = currentBook?.spreads || {};
      const sortedSpreads = Object.entries(spreads).sort(([a], [b]) => {
        const ai = parseInt(String(a).split('-')[1] || '0', 10);
        const bi = parseInt(String(b).split('-')[1] || '0', 10);
        return ai - bi;
      });
      const items = [];
      sortedSpreads.forEach(([spreadId, spread]) => {
        const els = (spread && spread.elements) || {};
        const textWithAudio = Object.entries(els)
          .filter(([_, el]) => el?.type === 'text' && el?.audio?.url)
          .map(([elementId, el]) => ({ elementId, el }));

        if (!textWithAudio.length) return;

        // 1) If explicit order exists on all, respect it
        const allHaveOrder = textWithAudio.every(({ el }) => typeof el?.audio?.order === 'number');
        let ordered;
        if (allHaveOrder) {
          ordered = textWithAudio.slice().sort((a, b) => (a.el.audio.order || 0) - (b.el.audio.order || 0));
        } else {
          // 2) Visual ordering heuristic
          const xs = textWithAudio.map(({ el }) => Number(el?.position?.x || 0)).sort((a, b) => a - b);
          const medianX = xs.length ? xs[Math.floor(xs.length / 2)] : 0;
          const left = [];
          const right = [];
          textWithAudio.forEach((t) => {
            const x = Number(t.el?.position?.x || 0);
            if (x <= medianX) left.push(t); else right.push(t);
          });
          const orderWithinSide = (arr) => arr.sort((a, b) => {
            const ay = Number(a.el?.position?.y || 0);
            const by = Number(b.el?.position?.y || 0);
            if (ay !== by) return ay - by;
            const ax = Number(a.el?.position?.x || 0);
            const bx = Number(b.el?.position?.x || 0);
            return ax - bx;
          });
          ordered = [...orderWithinSide(left), ...orderWithinSide(right)];
        }

        ordered.forEach(({ elementId, el }) => {
          const title = el?.content ? String(el.content).slice(0, 40) + (String(el.content).length > 40 ? '…' : '') : 'Text';
          items.push({ id: `${spreadId}:${elementId}`, title, url: el.audio.url, durationSec: el?.audio?.durationSec });
        });
      });
      return items;
    } catch {
      return [];
    }
  }, [currentBook?.spreads]);

  const didRequestPromptsRef = React.useRef(false);
  const ensurePromptsUnsubRef = React.useRef(null);
  const lastObservedStatusRef = React.useRef(null);
  const lastObservedBookStatusRef = React.useRef(null);
  // Only trigger ensure when we truly observe a transition to completed
  const bookStatusSawNonCompletedRef = React.useRef(false);
  const hasRetriedRef = React.useRef(false);
  const didFireEnsureOnCompletionRef = React.useRef(false);
  const pendingSignatureRef = React.useRef(null);
  // Track last observed world/character signature to detect edits while on Design
  const lastWorldSigRef = React.useRef(null);
  // Keep a stable reference to ensurePromptsOnce to avoid TDZ and stale closures
  const ensurePromptsOnceRef = React.useRef(null);

  const stableStringifyForPromptsSignature = useCallback((obj) => {
    const seen = new WeakSet();
    const sorter = (value) => {
      if (value === null || typeof value !== 'object') return value;
      if (seen.has(value)) return null;
      seen.add(value);
      if (Array.isArray(value)) {
        const mapped = value.map((v) => sorter(v));
        if (
          mapped.length &&
          typeof mapped[0] === 'object' &&
          mapped[0] &&
          (mapped[0].name || mapped[0].mainName)
        ) {
          return mapped
            .slice()
            .sort((a, b) => String((a && (a.name || a.mainName)) || '').localeCompare(String((b && (b.name || b.mainName)) || '')));
        }
        return mapped;
      }
      const out = {};
      Object.keys(value)
        .sort()
        .forEach((k) => {
          if (k === 'imageUrl' || k === 'updatedAt' || k === 'createdAt' || k === 'id' || k === '_id') return;
          out[k] = sorter(value[k]);
        });
      return out;
    };
    return JSON.stringify(sorter(obj || {}));
  }, []);

  const sha256Hex = useCallback(async (input) => {
    try {
      const enc = new TextEncoder();
      const bytes = enc.encode(String(input || ''));
      const digest = await crypto.subtle.digest('SHA-256', bytes);
      return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch (_) {
      return null;
    }
  }, []);

  const shouldEnsureDesignPrompts = useCallback(async () => {
    if (!bookId) return false;
    if (!currentBook || currentBook.id !== bookId) return false;
    if (currentBook.status !== 'completed') return false;
    const worldObj = (currentBook.world && typeof currentBook.world === 'object') ? currentBook.world : {};
    const canonical = stableStringifyForPromptsSignature(worldObj || {});
    const sig = await sha256Hex(canonical);
    if (!sig) return true; // if hashing fails, fall back to running (backend will NOOP if up-to-date)
    // If we're already waiting for this signature to land on the book, do not fire again
    if (pendingSignatureRef.current && pendingSignatureRef.current === sig) {
      console.log('[ensureDesignPrompts] gate: pending signature in-flight, skipping duplicate');
      return false;
    }
    const lastSig = currentBook.promptsSignature || null;
    const changed = lastSig !== sig;
    console.log(`[ensureDesignPrompts] gate: book completed, signature changed=${changed} (last=${lastSig}, now=${sig})`);
    return changed;
  }, [bookId, currentBook, stableStringifyForPromptsSignature, sha256Hex]);

  useEffect(() => {
    didRequestPromptsRef.current = false;
    hasRetriedRef.current = false;
    didFireEnsureOnCompletionRef.current = false;
    if (ensurePromptsUnsubRef.current) {
      try { ensurePromptsUnsubRef.current(); } catch (_) {}
      ensurePromptsUnsubRef.current = null;
    }
  }, [bookId]);

  // Clear pending when the book reflects the expected signature
  useEffect(() => {
    if (!currentBook || !currentBook.promptsSignature) return;
    if (pendingSignatureRef.current && currentBook.promptsSignature === pendingSignatureRef.current) {
      console.log('[ensureDesignPrompts] observed promptsSignature landed; clearing pending');
      pendingSignatureRef.current = null;
      didRequestPromptsRef.current = false;
      hasRetriedRef.current = false;
    }
  }, [currentBook?.promptsSignature]);

  // DISABLED: Detect world/character changes while on Design and book already completed,
  // and run ensureDesignPrompts once per new signature.
  // useEffect(() => {
  //   let cancelled = false;
  //   (async () => {
  //     if (!bookId) return;
  //     if (currentBook?.id !== bookId) return;
  //     if (currentBook?.status !== 'completed') return;
  //     // Build canonical signature of world or characterData
  //     const source = currentBook?.world || currentBook?.characterData || {};
  //     let obj = source;
  //     if (typeof source === 'string') {
  //       try { obj = JSON.parse(source); } catch (_) { obj = {}; }
  //     }
  //     const canonical = stableStringifyForPromptsSignature(obj || {});
  //     const sig = await sha256Hex(canonical);
  //     if (!sig) return;
  //     // Initialize on first observe without firing
  //     if (lastWorldSigRef.current == null) {
  //       lastWorldSigRef.current = sig;
  //       return;
  //     }
  //     // If world signature changed and book not yet updated to that promptsSignature, trigger ensure
  //     if (!cancelled && sig !== lastWorldSigRef.current) {
  //       // If the book already carries this signature, no need to run
  //       if (currentBook?.promptsSignature === sig) {
  //         lastWorldSigRef.current = sig;
  //         return;
  //       }
  //       // If we're already waiting for this signature, do not duplicate
  //       if (pendingSignatureRef.current && pendingSignatureRef.current === sig) {
  //         return;
  //       }
  //       lastWorldSigRef.current = sig;
  //       // DISABLED: Automatic ensureDesignPrompts on world/character changes
  //       // try { await ensurePromptsOnceRef.current?.(); } catch (_) {}
  //     }
  //   })();
  //   return () => { cancelled = true; };
  // }, [bookId, currentBook?.id, currentBook?.status, currentBook?.world, currentBook?.characterData, stableStringifyForPromptsSignature, sha256Hex]);

  // Stabilize function identities to prevent useEffect loops
  const loadBookRef = React.useRef(loadBook);
  React.useEffect(() => { loadBookRef.current = loadBook; }, [loadBook]);
  const updatePageSizeRef = React.useRef(updatePageSize);
  React.useEffect(() => { updatePageSizeRef.current = updatePageSize; }, [updatePageSize]);

  const ensurePromptsOnce = useCallback(async () => {
    if (!bookId) return;
    if (didRequestPromptsRef.current) return;
    const shouldRun = await shouldEnsureDesignPrompts();
    if (!shouldRun) {
      console.log(`[ensureDesignPrompts] skipped (up-to-date or book not completed) for ${bookId} at`, new Date().toISOString());
      return;
    }
    // Fast check-only call: do not show loading overlay for this.
    const checkRes = /** @type {any} */ (await ensureDesignPromptsCheck(bookId));
    const checkSkipped =
      !!checkRes && typeof checkRes === 'object' && 'skipped' in checkRes && /** @type {any} */ (checkRes).skipped === true;
    const checkReason =
      !!checkRes && typeof checkRes === 'object' && 'reason' in checkRes ? /** @type {any} */ (checkRes).reason : null;
    if (checkSkipped) {
      console.log(`[ensureDesignPrompts] check skipped (${checkReason || 'unknown'}) for ${bookId} at`, new Date().toISOString());
      if (checkReason === 'job_running' && !hasRetriedRef.current) {
        console.log('[ensureDesignPrompts] check busy, retrying shortly (once path)');
        hasRetriedRef.current = true;
        setTimeout(() => { try { ensurePromptsDirect(); } catch (_) {} }, 700);
      }
      return;
    }
    // Capture the signature we expect the backend to set
    try {
      const worldObj = (currentBook?.world && typeof currentBook.world === 'object') ? currentBook.world : {};
      const canonical = stableStringifyForPromptsSignature(worldObj || {});
      const sig = await sha256Hex(canonical);
      if (sig) pendingSignatureRef.current = sig;
    } catch (_) {}
    didRequestPromptsRef.current = true;
    try {
      console.log(`[ensureDesignPrompts] starting for ${bookId} at`, new Date().toISOString());
      startEnsure();
      // REMOVED: const res = await ensureDesignPrompts(bookId);
      // If backend returns skipped/noop, allow future runs on new changes
      const didSkip = false; // REMOVED: !!res && typeof res === 'object' && 'skipped' in res && /** @type {any} */ (res).skipped === true;
      const reason = null; // REMOVED: (!!res && typeof res === 'object' && 'reason' in res) ? /** @type {any} */ (res).reason : null;
      if (didSkip && reason === 'job_running' && !hasRetriedRef.current) {
        console.log('[ensureDesignPrompts] busy, retrying shortly (once path)');
        didRequestPromptsRef.current = false;
        hasRetriedRef.current = true;
        setTimeout(() => { try { ensurePromptsDirect(); } catch (_) {} }, 700);
        return;
      }
      if (didSkip) didRequestPromptsRef.current = false;
      try {
        await loadBookRef.current(bookId);
      } catch (_) {
        // Non-blocking
      }
      // loadBook can be skipped due to in-flight guard; do a delayed retry refresh
      setTimeout(() => {
        try { loadBookRef.current(bookId); } catch (_) {}
      }, 1200);
      setDesignRefreshNonce((n) => n + 1);
      console.log(`[ensureDesignPrompts] finished for ${bookId} at`, new Date().toISOString());
    } finally {
      finishEnsure();
    }
  }, [bookId, shouldEnsureDesignPrompts]);

  // DISABLED: Automatic ensureDesignPrompts sync
  // React.useEffect(() => { ensurePromptsOnceRef.current = ensurePromptsOnce; }, [ensurePromptsOnce]);

  // Bypass-gated trigger: used when we observe processing doc reach 'completed'
  // to avoid timing issues where books.status hasn't reflected yet.
  const ensurePromptsBypassGate = useCallback(async () => {
    if (!bookId) return;
    if (didRequestPromptsRef.current) return;
    // Fast check-only call: do not show loading overlay for this.
    const checkRes = /** @type {any} */ (await ensureDesignPromptsCheck(bookId));
    const checkSkipped =
      !!checkRes && typeof checkRes === 'object' && 'skipped' in checkRes && /** @type {any} */ (checkRes).skipped === true;
    const checkReason =
      !!checkRes && typeof checkRes === 'object' && 'reason' in checkRes ? /** @type {any} */ (checkRes).reason : null;
    if (checkSkipped) {
      console.log(`[ensureDesignPrompts] bypass check skipped (${checkReason || 'unknown'}) for ${bookId} at`, new Date().toISOString());
      if (checkReason === 'job_running' && !hasRetriedRef.current) {
        hasRetriedRef.current = true;
        setTimeout(() => { try { ensurePromptsBypassGate(); } catch (_) {} }, 700);
      }
      return;
    }
    // Capture signature for pending guard (best-effort)
    try {
      const worldObj = (currentBook?.world && typeof currentBook.world === 'object') ? currentBook.world : {};
      const canonical = stableStringifyForPromptsSignature(worldObj || {});
      const sig = await sha256Hex(canonical);
      if (sig) pendingSignatureRef.current = sig;
    } catch (_) {}
    didRequestPromptsRef.current = true;
    try {
      startEnsure();
      // REMOVED: await /** @type {any} */ (ensureDesignPrompts(bookId));
      try { await loadBookRef.current(bookId); } catch (_) {}
      setTimeout(() => { try { loadBookRef.current(bookId); } catch (_) {} }, 1200);
      setDesignRefreshNonce((n) => n + 1);
    } finally {
      finishEnsure();
    }
  }, [bookId, stableStringifyForPromptsSignature, sha256Hex]);

  // DISABLED: Secondary safety: once ensurePromptsOnce is defined, if the book is already completed
  // and we haven't fired yet, run ensurePromptsOnce now. This covers the case where
  // the initial effect ran before the ref was populated.
  // useEffect(() => {
  //   if (!bookId) return;
  //   if (currentBook?.id !== bookId) return;
  //   if (currentBook?.status !== 'completed') return;
  // // DISABLED: Automatic ensureDesignPrompts on completion
  // // if (!didFireEnsureOnCompletionRef.current) {
  // //   didFireEnsureOnCompletionRef.current = true;
  // //   ensurePromptsOnce();
  // // }
  // }, [bookId, currentBook?.id, currentBook?.status, ensurePromptsOnce]);

  // Backup path: if status tracking context reports 'completed' and no ensure in-flight, trigger bypass ensure.
  useEffect(() => {
    if (!bookId) return;
    const st = bookProcessingStatuses?.[bookId]?.status;
    if (st === 'completed' && !didRequestPromptsRef.current) {
      console.log('[ensureDesignPrompts] status-tracker path triggering bypass ensure');
      ensurePromptsBypassGate();
    }
  }, [bookId, bookProcessingStatuses, ensurePromptsBypassGate]);

  // Immediate, un-gated trigger: use when we get an authoritative 'completed' event.
  const ensurePromptsDirect = useCallback(async () => {
    if (!bookId) return;
    if (didRequestPromptsRef.current) return;
    // Avoid pointless calls if signature didn't change or book not completed yet
    const run = await shouldEnsureDesignPrompts();
    if (!run) {
      console.log(`[ensureDesignPrompts] direct path gated off (not needed) for ${bookId} at`, new Date().toISOString());
      return;
    }
    // Fast check-only call: do not show loading overlay for this.
    const checkRes = /** @type {any} */ (await ensureDesignPromptsCheck(bookId));
    const checkSkipped =
      !!checkRes && typeof checkRes === 'object' && 'skipped' in checkRes && /** @type {any} */ (checkRes).skipped === true;
    const checkReason =
      !!checkRes && typeof checkRes === 'object' && 'reason' in checkRes ? /** @type {any} */ (checkRes).reason : null;
    if (checkSkipped) {
      console.log(`[ensureDesignPrompts] direct check skipped (${checkReason || 'unknown'}) for ${bookId} at`, new Date().toISOString());
      if (checkReason === 'job_running' && !hasRetriedRef.current) {
        console.log('[ensureDesignPrompts] check busy, retrying shortly (direct path)');
        hasRetriedRef.current = true;
        setTimeout(() => { try { ensurePromptsDirect(); } catch (_) {} }, 700);
      }
      return;
    }
    // Capture signature for pending guard
    try {
      const worldObj = (currentBook?.world && typeof currentBook.world === 'object') ? currentBook.world : {};
      const canonical = stableStringifyForPromptsSignature(worldObj || {});
      const sig = await sha256Hex(canonical);
      if (sig) pendingSignatureRef.current = sig;
    } catch (_) {}
    didRequestPromptsRef.current = true;
    try {
      console.log(`[ensureDesignPrompts] direct trigger starting for ${bookId} at`, new Date().toISOString());
      startEnsure();
      // REMOVED: const res = /** @type {any} */ (await ensureDesignPrompts(bookId));
      const didSkip = false; // REMOVED: !!res && typeof res === 'object' && 'skipped' in res && /** @type {any} */ (res).skipped === true;
      const reason = null; // REMOVED: (!!res && typeof res === 'object' && 'reason' in res) ? /** @type {any} */ (res).reason : null;
      if (didSkip && reason === 'job_running' && !hasRetriedRef.current) {
        console.log('[ensureDesignPrompts] busy, retrying shortly (direct path)');
        didRequestPromptsRef.current = false;
        hasRetriedRef.current = true;
        setTimeout(() => { try { ensurePromptsDirect(); } catch (_) {} }, 700);
        return;
      }
      if (didSkip) didRequestPromptsRef.current = false;
      try { await loadBookRef.current(bookId); } catch (_) {}
      setTimeout(() => { try { loadBookRef.current(bookId); } catch (_) {} }, 1200);
      setDesignRefreshNonce((n) => n + 1);
      console.log(`[ensureDesignPrompts] direct trigger finished for ${bookId} at`, new Date().toISOString());
    } finally {
      finishEnsure();
    }
  }, [bookId]);

  // Run initial ensureDesignPromptsCheck on page load (once only)
  useEffect(() => {
    if (!bookId || initialCheckRunRef.current) return;
    
    initialCheckRunRef.current = true;
    (async () => {
      try {
        await ensureDesignPromptsCheck(bookId);
      } catch (e) {
        console.error('Initial ensureDesignPromptsCheck error:', e);
      } finally {
        setInitialCheckDone(true);
      }
    })();
  }, [bookId]);

  // Handle scroll-to-element from notification links
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const elementId = params.get('scrollToElement');
    
    if (!elementId) return;
    
    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      const element = document.querySelector(`[data-element-id="${elementId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Add a brief highlight effect
        element.classList.add('ring-2', 'ring-indigo-500', 'ring-offset-2');
        setTimeout(() => {
          element.classList.remove('ring-2', 'ring-indigo-500', 'ring-offset-2');
        }, 2000);
        console.log('[DesignPage] Scrolled to element:', elementId);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [location.search]);

  // DISABLED: If the book is already completed when entering Design (common case),
  // start ensuring prompts immediately even if there's no processing doc.
  // useEffect(() => {
  //   if (!bookId) return;
  //   if (currentBook?.id !== bookId) return;
  //   if (currentBook?.status !== 'completed') return;
  // // DISABLED: Automatic ensureDesignPrompts backup path
  // // if (!didFireEnsureOnCompletionRef.current) {
  // //   didFireEnsureOnCompletionRef.current = true;
  // //   ensurePromptsOnceRef.current?.();
  // // }
  // }, [bookId, currentBook?.id, currentBook?.status]);

  // Precise route-local trigger: watch books/{bookId}.status and when it becomes 'completed'
  // while on /design, immediately run ensurePromptsDirect().
  useEffect(() => {
    if (!bookId) return;
    // Only attach on Design route
    if (!location?.pathname?.includes('/design')) return;
    let cancelled = false;
    const ref = doc(db, 'books', bookId);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const status = snap.data()?.status || null;
      const last = lastObservedBookStatusRef.current;
      // Mark that we've seen a non-completed state at least once while this listener is active
      if (status && status !== 'completed') {
        bookStatusSawNonCompletedRef.current = true;
      }
      // Only fire ensure on a real transition to completed (avoid firing on initial snapshot already completed)
      if (
        status !== last &&
        status === 'completed' &&
        bookStatusSawNonCompletedRef.current &&
        !cancelled
      ) {
        console.log(`[SBW:DesignPage] books/${bookId} status became completed at`, new Date().toISOString());
        if (!didFireEnsureOnCompletionRef.current) {
          didFireEnsureOnCompletionRef.current = true;
          ensurePromptsDirect();
        }
        // Unsubscribe after first trigger to avoid repeated firings on unrelated updates
        try { unsub(); } catch (_) {}
        return;
      }
      lastObservedBookStatusRef.current = status;
    }, (err) => {
      console.error('[SBW:DesignPage] books status listener error:', err);
    });
    return () => { cancelled = true; try { unsub(); } catch (_) {} };
  }, [bookId, location?.pathname, ensurePromptsDirect]);

  // Immediately force the image placeholder when arriving on /design while
  // the book is not completed yet (or not loaded yet). Cleared by the
  // processing listener below or when the book becomes completed.
  useEffect(() => {
    if (!location?.pathname?.includes('/design')) return;
    try {
      if (typeof window !== 'undefined') {
        const notCompleted = !currentBook || (currentBook.status && currentBook.status !== 'completed');
        console.log('[SBW:DesignPage] Checking placeholder on mount/status change:', {
          bookId,
          currentBookStatus: currentBook?.status,
          notCompleted,
          pathname: location?.pathname,
          timestamp: new Date().toISOString()
        });
        if (notCompleted) {
          window.__sbwForcePlaceholder = true;
          if (!window.__sbwLoadingMessage) {
            window.__sbwLoadingMessage = { progress: 20, message: 'Preparing design…' };
          }
          console.log('[SBW:DesignPage] Set force placeholder:', {
            forcePlaceholder: window.__sbwForcePlaceholder,
            loadingMessage: window.__sbwLoadingMessage
          });
        }
      }
    } catch (_) {}
  }, [currentBook?.status, location?.pathname, bookId]);

  useEffect(() => {
    if (!bookId) return;
    let cancelled = false;

    // Listen to the latest processing doc for this book (doc id may not equal bookId).
    // Using updatedAt keeps this compatible with updateBookWithPreviewTwoHttp which writes updatedAt.
    const q = query(
      collection(db, 'bookWithPreviewProcessing'),
      where('bookId', '==', bookId),
      orderBy('updatedAt', 'desc'),
      limit(1)
    );

    const unsub = onSnapshot(
      q,
      (qs) => {
        if (qs.empty) {
          // No processing doc found; fall back to already-completed book path.
          if (!cancelled && currentBook?.id === bookId && currentBook?.status === 'completed') {
            console.log(`[SBW:DesignPage] no processing doc; book already completed for ${bookId} at`, new Date().toISOString());
            // DISABLED: Automatic ensureDesignPrompts fallback
            // ensurePromptsOnceRef.current?.();
            try { unsub(); } catch (_) {}
            ensurePromptsUnsubRef.current = null;
          }
          // Clear global placeholder override when there is no processing doc
          try { if (typeof window !== 'undefined') { window.__sbwForcePlaceholder = false; window.__sbwLoadingMessage = null; } } catch (_) {}
          return;
        }
        const d = qs.docs[0];
        const data = d.data();
        const st = data?.status || null;
        console.log('[SBW:DesignPage] Processing doc update:', {
          status: st,
          loadingUIMessage: data.loadingUIMessage,
          timestamp: new Date().toISOString()
        });
        // Force placeholder immediately on /design while processing is not completed
        try {
          if (typeof window !== 'undefined') {
            if (st && st !== 'completed') {
              window.__sbwForcePlaceholder = true;
              // Always update with the dynamic loading message from the processing document
              // This ensures the message updates as the backend progresses through stages
              window.__sbwLoadingMessage = data.loadingUIMessage || { progress: 25, message: 'Preparing design…' };
              console.log('[SBW:DesignPage] Set window.__sbwLoadingMessage:', window.__sbwLoadingMessage);
            } else if (st === 'completed') {
              window.__sbwForcePlaceholder = false;
              window.__sbwLoadingMessage = null;
            }
          }
        } catch (_) {}
        if (st === 'completed') {
          console.log(`[SBW:DesignPage] observed processing completed for ${bookId} (doc ${d.id}) at`, new Date().toISOString());
          // Clear force placeholder on completion
          try { if (typeof window !== 'undefined') { window.__sbwForcePlaceholder = false; window.__sbwLoadingMessage = null; } } catch (_) {}
          // Guarantee at least one ensure attempt from the processing path.
          // Use didRequestPromptsRef (the true de-dupe guard) instead of didFireEnsureOnCompletionRef.
          (async () => {
            try { await loadBookRef.current?.(bookId); } catch (_) {}
            setTimeout(() => {
              console.log('[SBW:DesignPage] processing-completed path invoking bypass ensure');
              try { ensurePromptsBypassGate(); } catch (_) {}
            }, 400);
          })();
          didFireEnsureOnCompletionRef.current = true;
          try { unsub(); } catch (_) {}
          ensurePromptsUnsubRef.current = null;
        } else if (st === 'error') {
          console.log(`[SBW:DesignPage] observed processing error for ${bookId} (doc ${d.id}) at`, new Date().toISOString());
          // Clear force placeholder on error
          try { if (typeof window !== 'undefined') { window.__sbwForcePlaceholder = false; window.__sbwLoadingMessage = null; } } catch (_) {}
          didFireEnsureOnCompletionRef.current = true;
          try { unsub(); } catch (_) {}
          ensurePromptsUnsubRef.current = null;
        }
      },
      (err) => {
        console.error('[SBW:DesignPage] snapshot error for processing listener:', err);
      }
    );
    ensurePromptsUnsubRef.current = unsub;

    return () => {
      cancelled = true;
      if (ensurePromptsUnsubRef.current) {
        try { ensurePromptsUnsubRef.current(); } catch (_) {}
        ensurePromptsUnsubRef.current = null;
      }
    };
  }, [bookId]);
  
  // Make current status available globally for toast decisions
  // Add JSDoc comments to fix TypeScript lint error
  /**
   * @type {(id?: string) => string | null}
   */
  // @ts-ignore - Extend Window type for our global functions

  window.getCurrentProcessingStatus = (id) => {
    const bookId = id || (currentBook ? currentBook.id : null);
    return bookId ? bookProcessingStatuses[bookId]?.status : null;
  };
  
  // CONSOLIDATED TOAST MANAGEMENT - Single source for all toast notifications
  useEffect(() => {
    // Only proceed if we have a valid bookId
    if (!bookId) return;
    
    // Check if we're coming from navigation with a notification state
    const showFromNavigation = location.state?.showNotification;
    
    // Get current processing status
    const currentStatus = bookProcessingStatuses[bookId]?.status;
    console.log(`🔄 TOAST MANAGER: Book ${bookId} status is ${currentStatus}, from navigation: ${showFromNavigation}`);
    
    // Only show toast if we have a status or are coming from navigation
    if (currentStatus || showFromNavigation) {
      // Use the simplified global function for showing a single status toast
      // @ts-ignore - Access to global window function
      window.showDesignStatusToast?.(bookId, currentStatus || 'processing');
      setToastShown(true);
      
      // Fallback in case the global function doesn't work
      if (!window.showDesignStatusToast) {
        // Determine toast message based on status
        const messages = {
          generatingCharacter: 'Generating character images...',
          generatingCoverImage: 'Generating cover image...',
          updatingImageElements: 'Updating image elements...',
          completed: 'Design is ready!'
        };
        
        // Skip showing toast if status is completed
        if (currentStatus === 'completed') {
          notify.dismiss('design-status-toast');
        } else {
          const message = messages[currentStatus || 'processing'] || 'Processing your book...';
          
          // Show or update toast with consistent ID
          notify.info(message, {
            toastId: 'design-status-toast',
            autoClose: 8000,
            closeOnClick: true,
            draggable: true
          });
        }
      }
    }
    
    // Clean navigation state to prevent showing again on refresh
    if (showFromNavigation) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [bookId, bookProcessingStatuses, location, navigate]); // Re-run when relevant state changes

  // Secondary trigger removed for ensure to avoid duplicate fires; we still track status for UI.
  useEffect(() => {
    if (!bookId) return;
    const st = bookProcessingStatuses?.[bookId]?.status;
    if (!st) return;
    const last = lastObservedStatusRef.current;
    if (st !== last && st === 'completed') {
      console.log(`[ensureDesignPrompts] status tracker observed completed for ${bookId} at`, new Date().toISOString());
      // No ensure trigger here; books status listener handles the call
    }
    lastObservedStatusRef.current = st;
  }, [bookId, bookProcessingStatuses, ensurePromptsDirect]);

  // Safety check - if for some reason we don't have a book yet, load it again
  useEffect(() => {
    if (bookId && !currentBook && !loading && !error && initialLoadDoneRef.current) {
      console.log('No book found but we have a bookId - reloading book data');
      loadBook(bookId);
    }
  }, [bookId, currentBook, loading, error, loadBook]);

  // Navigation state is now handled by the consolidated toast management useEffect

  // Status tracking is now handled by the consolidated toast management useEffect

  // Last resort functionality is now handled by the consolidated toast management useEffect

  // POST-LOADING TOAST is now handled by the consolidated toast management useEffect
  // Ultra-aggressive toast notification system is now handled by the consolidated toast management useEffect

  useEffect(() => {
    if (bookId) {
      // Load book from database (which will include its pageSize)
      if (!initialLoadDoneRef.current) {
        initialLoadDoneRef.current = true;
        setTimeout(() => {
          console.log('DesignPage: Initial delayed loadBook firing for', bookId);
          loadBookRef.current?.(bookId);
        }, 1000);
      } else {
        loadBookRef.current?.(bookId);
      }
      
      // On initial load, immediately load page size from local storage if it exists
      const storedPageSize = localStorage.getItem(`book_page_size_${bookId}`);
      if (storedPageSize) {
        console.log(`DesignPage: Found stored page size ${storedPageSize} for book ${bookId}`); 
        updatePageSizeRef.current?.(storedPageSize, bookId);
      } else {
        console.log(`DesignPage: No stored page size found for book ${bookId}`);
      }

      // Only check for processing if we haven't already detected completion
      if (!completedBookIds.current[bookId] && !previewProcessingListener) {
        checkForExistingPreviewProcessing();
      }
    }

    // Clean up listener on unmount
    return () => {
      if (previewProcessingListener) {
        previewProcessingListener();
        setPreviewProcessingListener(null);
      }
    };
  }, [bookId]);
  
  // Update page size when book loads or changes
  useEffect(() => {
    if (currentBook?.id && currentBook.pageSize) {
      console.log(`DesignPage: Book loaded with page size: ${currentBook.pageSize}`);
      
      // Update the context state and ensure it's correctly synchronized with the database
      setCurrentBookPageSize(currentBook.id, currentBook.pageSize);
      updatePageSize(currentBook.pageSize, currentBook.id);
      
      // Ensure the page size is stored for this book ID
      localStorage.setItem(`book_page_size_${currentBook.id}`, currentBook.pageSize);
      sessionStorage.setItem('current_page_size', currentBook.pageSize);
    } else if (currentBook?.id && !currentBook.pageSize) {
      // Ensure a sane default when the book has no page size set yet
      console.log('DesignPage: No page size on book. Defaulting to us-trade (6 x 9 in).');
      // Set default in context storages
      setCurrentBookPageSize(currentBook.id, 'us-trade');
      // Persist to Firestore and local/session storage
      updatePageSize('us-trade', currentBook.id);
      localStorage.setItem(`book_page_size_${currentBook.id}`, 'us-trade');
      sessionStorage.setItem('current_page_size', 'us-trade');
    }
  }, [currentBook?.id, currentBook?.pageSize, updatePageSize, setCurrentBookPageSize]);

  const handleEnsurePrompts = useCallback(async () => {
    if (!bookId) return;
    const generatingToastId = 'design-prompts-generating';
    try {
      // Fast check-only call: do not show loading overlay for this.
      const checkRes = /** @type {any} */ (await ensureDesignPromptsCheck(bookId));
      const checkSkipped =
        !!checkRes && typeof checkRes === 'object' && 'skipped' in checkRes && /** @type {any} */ (checkRes).skipped === true;
      if (checkSkipped) {
        return;
      }

      // Use delayed overlay like automatic ensure paths to avoid flashes
      startEnsure();
      let resolved = false;
      const timerId = setTimeout(() => {
        if (!resolved) {
          notify.info('Generating image prompts...', {
            toastId: generatingToastId,
            autoClose: false,
            closeOnClick: false,
            draggable: false,
          });
        }
      }, 3000);

      const res = /** @type {any} */ (await ensureDesignPrompts(bookId));
      resolved = true;
      clearTimeout(timerId);
      notify.dismiss(generatingToastId);

      if (res?.success && !res?.skipped) {
        notify.success('Image prompts updated.', { toastId: 'design-prompts-success' });
        // Use ref to avoid changing dependencies
        await loadBookRef.current?.(bookId);
      } else if (res?.skipped) {
        // No toasts when skipped (up_to_date or job_running)
      } else {
        notify.error('Failed to update prompts.', { toastId: 'design-prompts-error' });
      }
    } catch (e) {
      notify.error('Error updating prompts', { toastId: 'design-prompts-error' });
      console.error('ensureDesignPrompts error', e);
    } finally {
      // Safety: ensure any generating toast is dismissed
      notify.dismiss(generatingToastId);
      finishEnsure();
    }
  }, [bookId, startEnsure, finishEnsure]);

  // NOTE: Do not auto-run ensureDesignPrompts on mount.
  // It must only run after preview generation is completed, handled by the controller above.

  // Setup a listener for bookWithPreviewProcessing document
  const setupPreviewProcessingListener = useCallback((processingId) => {
    if (!processingId || typeof processingId !== 'string') return null;
    if (previewProcessingListener) previewProcessingListener(); // Clean up previous listener
    
    console.log(`\ud83d\udd04 DesignPage: Setting up preview processing listener for ${processingId}`);

    // Create new listener
    const unsubscribe = onSnapshot(
      doc(db, "bookWithPreviewProcessing", processingId),
      (doc) => {
        console.log(`\ud83d\udd04 DesignPage: Preview processing listener fired for ${processingId}:`, 
                  doc.exists() ? 'document exists' : 'document does not exist');
        
        if (doc.exists()) {
          const data = doc.data();
          console.log('\ud83d\udd04 DesignPage: Preview processing document data:', data.status);
          
          // Update local state
          setPreviewProcessingStatus(data.status);
          
          // Update global status tracking
          if (bookId && data.status) {
            updateBookProcessingStatus(bookId, data.status);
          }
          
          // Handle completed status
          if (data.status === "completed") {
            console.log('\u2705 DesignPage: Book preview processing completed, refreshing book data');
            
            // Mark this book as completed to prevent further checks
            completedBookIds.current[bookId] = true;
            
            // Clean up listener - we don't need it anymore
            unsubscribe();
            setPreviewProcessingListener(null);
            
            // Reload book data to get updated elements including images
            loadBook(bookId).then(() => {
              console.log('\u2705 Book data refreshed successfully after processing completion');
              
              // Show success toast
              notify.success("Book images generated successfully!", {
                toastId: 'design-images-generated'
              });
            });
          } else if (data.status === "error") {
            console.error('\u274c DesignPage: Book preview generation failed with error:', data.error);
            notify.error("Design Generation partially failed. Please try again.", { toastId: 'design-generation-error' });
          } else {
            // Status is pending, pending2, processing, or generatingCharacter
            console.log(`\ud83d\udd04 DesignPage: Book preview generation in progress - status: ${data.status}`);
            
            // Show toast for in-progress states with appropriate message
            const statusMessages = {
              "initializing": "Initializing image generation...",
              "generatingImagePrompts": "Generating image prompts...",
              "generatingCoverImage": "Creating your book cover...",
              "updatingImageElements": "Adding images to your book..."
            };
            
            // For debugging
            console.log(`🔔 ATTEMPTING TO SHOW TOAST for status: ${data.status}`);
            
            // Status updates are handled by StatusTrackingContext via notify.workflowStatus
            
            // Center toast notifications removed as they're redundant with the messages on the right
            if (statusMessages[data.status]) {
              console.log(`ℹ️ Status update for ${data.status} (center toast removed)`);
            }
          }
        }
      },
      (error) => {
        console.error(`\u274c DesignPage: Error in preview processing listener for ${processingId}:`, error);
        notify.error("Error monitoring image generation progress", { toastId: 'design-listener-error' });
      }
    );
    
    // Save the unsubscribe function
    setPreviewProcessingListener(() => unsubscribe);
    console.log(`\ud83d\udd04 DesignPage: Preview processing listener setup complete for ${processingId}`);
    return unsubscribe;
  }, [bookId, loadBook, updateBookProcessingStatus]);

  // Check for existing or completed preview processing
  const checkForExistingPreviewProcessing = useCallback(async () => {
    // If we've already detected completion for this book, don't check again
    if (completedBookIds.current[bookId]) {
      console.log(`Skipping preview processing check - already completed for book ${bookId}`);
      return () => {};
    }
    
    try {
      console.log(`\ud83d\udd0d DesignPage: Checking for existing preview processing for book ${bookId}`);
      
      // First check for active processing
      const activeProcessingQuery = query(
        collection(db, "bookWithPreviewProcessing"),
        where("bookId", "==", bookId),
        where("status", "in", ["pending", "pending2", "processing", "generatingCharacter"]),
        orderBy("createdAt", "desc"),
        limit(1)
      );

      const activeSnapshot = await getDocs(activeProcessingQuery);
      
      if (!activeSnapshot.empty) {
        const processingDoc = activeSnapshot.docs[0];
        const processingData = processingDoc.data();
        
        console.log(`\ud83d\udd0d DesignPage: Found active preview processing: ${processingDoc.id} with status: ${processingData.status}`);
        
        setPreviewProcessingId(processingDoc.id);
        setPreviewProcessingStatus(processingData.status);
        
        // Show toast about the current status
        const statusMessages = {
          "pending": "Book preview generation is starting...",
          "pending2": "Your book elements are being prepared...",
          "processing": "Your book content is being processed...",
          "generatingCharacter": "Character images are being generated...",
          "generatingCoverImage": "Creating your book cover...",
          "updatingImageElements": "Adding images to your book..."
        };
        
        // For debugging
        console.log(`🔔 ATTEMPTING TO SHOW INITIAL TOAST for status: ${processingData.status}`);
        
        // Status updates are handled by StatusTrackingContext via notify.workflowStatus
        
        // Center toast notifications removed as they're redundant with the messages on the right
        if (statusMessages[processingData.status]) {
          console.log(`ℹ️ Initial status update for ${processingData.status} (center toast removed)`);
        }
        
        // Setup real-time listener for this document
        return setupPreviewProcessingListener(processingDoc.id);
      }

      // If no active processing found, check for recently completed processing
      const completedProcessingQuery = query(
        collection(db, "bookWithPreviewProcessing"),
        where("bookId", "==", bookId),
        where("status", "==", "completed"),
        orderBy("createdAt", "desc"),
        limit(1)
      );

      const completedSnapshot = await getDocs(completedProcessingQuery);
      
      if (!completedSnapshot.empty) {
        const completedDoc = completedSnapshot.docs[0];
        const completedData = completedDoc.data();
        const completionTime = completedData.updatedAt?.toDate() || new Date();
        const currentTime = new Date();
        const minutesSinceCompletion = (currentTime - completionTime) / (1000 * 60);
        
        console.log(`\ud83d\udd0d DesignPage: Found completed processing from ${minutesSinceCompletion.toFixed(1)} minutes ago`);
        
        // Mark this book as completed to prevent further checks
        completedBookIds.current[bookId] = true;
        
        // If recently completed (within last 10 minutes), refresh book data
        if (minutesSinceCompletion < 10) {
          console.log('\ud83d\udd04 DesignPage: Recently completed processing found, refreshing book data');
          loadBook(bookId).then(() => {
            console.log('\u2705 Book data refreshed successfully after finding completed processing');
            
            // Show success toast
            notify.success("Book elements loaded!", {
              toastId: 'design-elements-loaded'
            });
          });
        }
      }

      return () => {}; // Return empty function if no processing found
    } catch (error) {
      console.error("\u274c DesignPage: Error checking for preview processing:", error);
      return () => {};
    }
  }, [bookId, setupPreviewProcessingListener, loadBook]);

  const handleGenerateAllImages = () => {
    setIsGeneratingImages(true);
    notify.info('Images are on the way...', { toastId: 'design-generate-all-images' });
    // TODO: Implement image generation logic
    setTimeout(() => setIsGeneratingImages(false), 2000);
  };

  const handleOpenGenerateAllImagesModal = async () => {
    // Save the spread if it's dirty before opening the modal
    if (window.__sbwDesignIsDirty && window.__sbwDesignSave) {
      try {
        await window.__sbwDesignSave();
      } catch (error) {
        console.error('Error saving spread:', error);
        notify.error('Failed to save spread. Please try again.', { toastId: 'design-save-error' });
        return;
      }
    }

    // Check for important world objects missing reference images before bulk generation
    if (currentBook?.world) {
      const importantObjects = getImportantObjects(currentBook.world);
      const missingImages = importantObjects.filter(obj => !obj.referenceImage).length;
      if (missingImages > 0) {
        setShowConsistencyModal(true);
        return;
      }
    }

    // Open the modal after spread is saved
    setIsGenerateAllModalOpen(true);
  };

  const handleOpenGenerateAllAudioModal = async () => {
    // Save the spread if it's dirty before opening the modal
    if (window.__sbwDesignIsDirty && window.__sbwDesignSave) {
      try {
        await window.__sbwDesignSave();
      } catch (error) {
        console.error('Error saving spread:', error);
        notify.error('Failed to save spread. Please try again.', { toastId: 'design-save-error' });
        return;
      }
    }
    setIsGenerateAllAudioOpen(true);
  };
  
  
  // Test function to directly show a toast
  const testToastDisplay = () => {
    console.log('Manual test toast button clicked');
    notify.info('Test toast from button click', { toastId: 'design-test-toast-manual' });
  };

  if (loading && !currentBook) {
    return (
      <BookDesignTopNavLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
        </div>
      </BookDesignTopNavLayout>
    );
  }

  if (error) {
    return (
      <BookDesignTopNavLayout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-700">{error}</p>
            <div className="mt-4 flex gap-4">
              <button
                onClick={() => loadBook(bookId)}
                className="text-indigo-600 hover:text-indigo-500"
              >
                Refetch Book
              </button>
              <button
                onClick={() => navigate('/library')}
                className="text-indigo-600 hover:text-indigo-500"
              >
                Return to Library
              </button>
            </div>
          </div>
        </div>
      </BookDesignTopNavLayout>
    );
  }

  if (!currentBook) {
    return (
      <BookDesignTopNavLayout>
        <div className="text-center py-12">
          <p className="text-red-600">
            Book not found. Please edit your story first.
          </p>
          <button
            onClick={() => navigate(`/edit/${bookId}`)}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Go to Edit Page
          </button>
          <button
            onClick={() => loadBook(bookId)}
            className="mt-2 text-indigo-600 hover:text-indigo-500 block"
          >
            Refetch Book
          </button>
        </div>
      </BookDesignTopNavLayout>
    );
  }

  
  return (
    <BookDesignTopNavLayout>
      {/* Add our guaranteed direct notification that bypasses toast blocking */}
      <DirectNotification bookId={bookId} bookProcessingStatuses={bookProcessingStatuses} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Design Controls and Action Buttons */}
        <div className="flex justify-between items-center mb-8">
          <div className="hidden">
            <PageSizeSelector bookId={bookId} />
          </div>
          <div className="flex items-center gap-2 w-full">
            <button
              onClick={handleEnsurePrompts}
              disabled={isEnsuringPrompts}
              style={{ display: 'none' }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              {isEnsuringPrompts ? 'Adding Prompts...' : 'Add Image Prompts'}
            </button>
            <AudioPlaylistPlayer
              items={narrationItems}
              label="Full Narration"
              onOpenGenerateAudio={handleOpenGenerateAllAudioModal}
              onOpenOrder={(spreadId) => { setNarrationOrderSpreadId(spreadId); setIsNarrationOrderOpen(true); }}
            />
            <button
              onClick={handleOpenGenerateAllImagesModal}
              disabled={isGeneratingImages}
              className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              <BoltIcon className="h-5 w-5" />
              {isGeneratingImages ? 'Generating...' : 'Generate All Images'}
            </button>
          </div>
        </div>

        {/* Book Cover Design section removed - redundant with the design UI below */}

        {/* Book content */}
        <div className="mb-8 relative">
          {!initialCheckDone ? (
            <div className="flex justify-center items-center h-64 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-gray-500">Preparing design...</div>
            </div>
          ) : currentBook ? (
            <BookSpread
              key={`${bookId}:${initialSpreadId}:${designRefreshNonce}`}
              bookId={bookId}
              initialSpreadId={initialSpreadId}
              totalPages={currentBook.totalPages || 32}
              pageSize={currentBook.pageSize || 'us-trade'} // Default to US Trade (6 x 9 in) if no page size
              ensureOverlay={{
                active: isEnsuringPrompts,
                message: `Generating prompts using ${promptSourceLabel}`,
                progress: ensurePromptsProgress,
                customTitle: 'Generating New Image Prompts',
                customDescription: `Crafting detailed prompt descriptions from ${promptSourceLabel.toLowerCase()} for each illustration`,
                showSteps: false,
              }}
            />
          ) : (
            <div className="flex justify-center items-center h-64 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-gray-500">Loading book content...</div>
            </div>
          )}

          {/* Per-element ensure overlay handled inside each ImageElement via ensureOverlay prop */}
        </div>

      <GenerateAllImagesModal
        isOpen={isGenerateAllModalOpen}
        onClose={() => setIsGenerateAllModalOpen(false)}
        onConfirm={handleGenerateAllImages}
        isGenerating={isGeneratingImages}
      />
      <WorldObjectsConsistencyModal
        isOpen={showConsistencyModal}
        onClose={() => setShowConsistencyModal(false)}
        onContinue={() => {
          setShowConsistencyModal(false);
          setIsGenerateAllModalOpen(true);
        }}
        onShowWorldObjects={() => {
          setShowConsistencyModal(false);
          navigate(`/world/${bookId}`);
        }}
        missingImageCount={(() => {
          const importantObjects = getImportantObjects(currentBook?.world);
          return importantObjects.filter(obj => !obj.referenceImage).length;
        })()}
        totalCount={(() => {
          const importantObjects = getImportantObjects(currentBook?.world);
          return importantObjects.length;
        })()}
      />
      <GenerateAllAudioModal
        isOpen={isGenerateAllAudioOpen}
        onClose={() => setIsGenerateAllAudioOpen(false)}
      />
      <NarrationOrderModal
        isOpen={isNarrationOrderOpen}
        onClose={() => setIsNarrationOrderOpen(false)}
        spreadId={narrationOrderSpreadId || `spread-0`}
      />
    </div>

    {isLoadingPrint && (
      <LoadingOverlay
        states={PRINT_LOADING_STATES}
        onComplete={() => {
          setIsLoadingPrint(false);
          navigate('/print');
        }}
      />
    )}
  </BookDesignTopNavLayout>
);
}
